import { useMemo } from "react";
import { DateTime } from "luxon";

import { CurrentUserData, useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { SubscriptionEnum } from "lib/types/graphQLEnums";
import { DataTypeName, DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";

export const availableDataTypes: DataTypeName[] = [
  "TENDERS|PRE-TENDERS",
  "AWARDS",
  "BUYERS|SUPPLIERS",
  "CONTACTS",
  "FRAMEWORKS",
  "DOCUMENTS",
  "BIDS",
  "MARKET_ANALYSIS",
];

// Utility functions. These take the current user (from useCurrentUser) explicitly so they can be
// reused outside React (e.g. tracking) without reaching for a global.
export function getActiveDataTypes(user: CurrentUserData | undefined): DataTypeName[] {
  return (user?.activeDataTypes as DataTypeName[] | undefined) ?? [];
}

export function getSubscribedDataTypes(user: CurrentUserData | undefined): DataTypeName[] {
  return (user?.subscribedDataTypes as DataTypeName[] | undefined) ?? [];
}

export function getTrialDate(
  user: CurrentUserData | undefined,
  dateType: "trialStart" | "trialEnd",
): Date | null {
  const dateString = user?.company[dateType];
  return dateString ? new Date(dateString) : null;
}

export function getCompanyPaymentType(user: CurrentUserData | undefined): string | null {
  return user?.company.paymentType ?? null;
}

/**
 * Whether the user's company is on the given plan.
 *
 * Use this — not the user's own `subscription` — for features licensed to the company as a whole,
 * such as SSO: a company admin on a freemium seat should still be able to configure them.
 */
export function companyHasSubscription(
  user: CurrentUserData | undefined | null,
  subscription: SubscriptionEnum,
): boolean {
  return user?.company.subscription === subscription;
}

export function sortDataTypes(dataTypes: DataTypeName[]): void {
  dataTypes.sort((a, b) => availableDataTypes.indexOf(a) - availableDataTypes.indexOf(b));
}

const toDataTypeName = (t: DataTypeNameWithBuyersAndSuppliers): DataTypeName =>
  t === "BUYERS" || t === "SUPPLIERS" ? "BUYERS|SUPPLIERS" : t;

function createHasDataTypes(activeDataTypes: DataTypeName[]) {
  return (...dataTypes: DataTypeNameWithBuyersAndSuppliers[]): boolean => {
    const missing = dataTypes.filter(
      (type) => activeDataTypes.indexOf(toDataTypeName(type)) === -1,
    );

    return missing.length === 0;
  };
}

function createHasSubscribedDataTypes(subscribedDataTypes: DataTypeName[]) {
  return (...dataTypes: DataTypeNameWithBuyersAndSuppliers[]): boolean => {
    return dataTypes.every((type) => subscribedDataTypes.includes(toDataTypeName(type)));
  };
}

function createGetMissingDataTypes(activeDataTypes: DataTypeName[]) {
  return (
    ...dataTypes: DataTypeNameWithBuyersAndSuppliers[]
  ): DataTypeNameWithBuyersAndSuppliers[] => {
    return dataTypes.filter((type) => activeDataTypes.indexOf(toDataTypeName(type)) === -1);
  };
}

export function isEligibleForTrial(
  trialEndDate: Date | null,
  companyPaymentType: string | null,
): boolean {
  return !trialEndDate && companyPaymentType !== "Paid";
}

export function isOnTrial(trialEndDate: Date | null): boolean {
  if (!trialEndDate) return false;
  const endOfTrialTime = DateTime.fromJSDate(trialEndDate).toUTC().endOf("day");
  const now = DateTime.now().toUTC();
  return now <= endOfTrialTime;
}

export function getTrialStatus(
  trialEndDate: Date | null,
  companyPaymentType: string | null,
): "On Trial" | "Eligible" | "Ineligible" {
  if (isOnTrial(trialEndDate)) {
    return "On Trial";
  } else if (isEligibleForTrial(trialEndDate, companyPaymentType)) {
    return "Eligible";
  } else {
    return "Ineligible";
  }
}

export function useSubscription() {
  const { data: currentUser } = useCurrentUser();

  // memoise for reference equality
  return useMemo(() => {
    const activeDataTypes = getActiveDataTypes(currentUser);
    const subscribedDataTypes = getSubscribedDataTypes(currentUser);
    const trialStartDate = getTrialDate(currentUser, "trialStart");
    const trialEndDate = getTrialDate(currentUser, "trialEnd");
    const companyPaymentType = getCompanyPaymentType(currentUser);
    const hasDataTypes = createHasDataTypes(activeDataTypes);
    const hasSubscribedDataTypes = createHasSubscribedDataTypes(subscribedDataTypes);
    const getMissingDataTypes = createGetMissingDataTypes(activeDataTypes);
    const deactivatedDataTypes = availableDataTypes.filter(
      (type) => !activeDataTypes.includes(type),
    );

    return {
      activatedDataTypes: activeDataTypes,
      deactivatedDataTypes,
      hasDataTypes,
      hasSubscribedDataTypes,
      getMissingDataTypes,
      trialEndDate,
      trialStartDate,
      companyPaymentType,
      eligibleForTrial: isEligibleForTrial(trialEndDate, companyPaymentType),
      isOnTrial: isOnTrial(trialEndDate),
      trialStatus: getTrialStatus(trialEndDate, companyPaymentType),
    };
  }, [currentUser]);
}
